/**
 * Rovasys company information — single source of truth for all document templates.
 */
export const ROVASYS = {
    name: 'Rovasys Allied Works Pvt. Ltd.',
    shortName: 'Rovasys',
    address: 'W-145, S-Block, MIDC-Bhosari,\nPune, Maharashtra - 411026',
    gstin: '27AAMCR6049R1ZD',
    pan: 'AAMCR6049R',
    stateCode: '27',
    state: 'Maharashtra',
    phone: '+91 91722 01844',
    email: 'info@rovasys.com',
    website: 'www.rovasys.com',
    logoPath: '/logos/rovasys_horizontal_clean.png',
    stampPath: '/img/rovasys-stamp.png',
    bankName: 'HDFC Bank',
    bankAccount: '50200055555555',
    bankIfsc: 'HDFC0000001',
    bankBranch: 'Bhosari, Pune',
} as const;

export const UOM_CHOICES = [
    { id: 'NOS', name: 'NOS – Numbers' },
    { id: 'PCS', name: 'PCS – Pieces' },
    { id: 'SET', name: 'SET – Sets' },
    { id: 'PRS', name: 'PRS – Pairs' },
    { id: 'KGS', name: 'KGS – Kilograms' },
    { id: 'GMS', name: 'GMS – Grammes' },
    { id: 'MTS', name: 'MTS – Metric Tonnes' },
    { id: 'QTL', name: 'QTL – Quintals' },
    { id: 'LTR', name: 'LTR – Litres' },
    { id: 'MLT', name: 'MLT – Millilitres' },
    { id: 'MTR', name: 'MTR – Metres' },
    { id: 'KME', name: 'KME – Kilometres' },
    { id: 'SQM', name: 'SQM – Square Metres' },
    { id: 'SQF', name: 'SQF – Square Feet' },
    { id: 'CBM', name: 'CBM – Cubic Metres' },
    { id: 'TON', name: 'TON – Tonnes' },
    { id: 'BOX', name: 'BOX – Box' },
    { id: 'ROL', name: 'ROL – Rolls' },
    { id: 'BAG', name: 'BAG – Bags' },
    { id: 'DRM', name: 'DRM – Drums' },
    { id: 'PAC', name: 'PAC – Packets' },
    { id: 'BDL', name: 'BDL – Bundles' },
    { id: 'CTN', name: 'CTN – Cartons' },
    { id: 'TUB', name: 'TUB – Tubes' },
    { id: 'OTH', name: 'OTH – Others' },
];

// Full Incoterms 2020 set + domestic delivery instructions (PO-10).
export const INCOTERM_CHOICES = [
    { id: 'EXW', name: 'EXW – Ex Works' },
    { id: 'FCA', name: 'FCA – Free Carrier' },
    { id: 'FAS', name: 'FAS – Free Alongside Ship' },
    { id: 'FOB', name: 'FOB – Free On Board' },
    { id: 'CFR', name: 'CFR – Cost and Freight' },
    { id: 'CIF', name: 'CIF – Cost, Insurance and Freight' },
    { id: 'CPT', name: 'CPT – Carriage Paid To' },
    { id: 'CIP', name: 'CIP – Carriage and Insurance Paid To' },
    { id: 'DPU', name: 'DPU – Delivered at Place Unloaded' },
    { id: 'DAP', name: 'DAP – Delivered at Place' },
    { id: 'DDP', name: 'DDP – Delivered Duty Paid' },
    { id: 'Door Delivery', name: 'Door Delivery' },
    { id: 'At-Site', name: 'At-Site' },
];

// Mode of transport — structured dropdown (DC-2).
export const MODE_OF_TRANSPORT_CHOICES = [
    { id: 'By Road', name: 'By Road' },
    { id: 'By Rail', name: 'By Rail' },
    { id: 'By Air', name: 'By Air' },
    { id: 'By Sea', name: 'By Sea' },
    { id: 'By Courier', name: 'By Courier' },
    { id: 'Hand Delivery', name: 'Hand Delivery' },
    { id: 'Other', name: 'Other' },
];

// Delivery terms for the Delivery Challan (ID:26). Covers domestic dispatch
// terms and export/import Incoterms so the same field serves both flows.
export const DELIVERY_TERMS_CHOICES = [
    // Domestic transport
    { id: 'Ex-Works', name: 'Domestic — Ex-Works (buyer arranges pickup)' },
    { id: 'FOR Destination', name: 'Domestic — FOR Destination (freight prepaid to site)' },
    { id: 'FOR Dispatch Station', name: 'Domestic — FOR Dispatch Station' },
    { id: 'Door Delivery', name: 'Domestic — Door Delivery' },
    { id: 'To Pay', name: 'Domestic — Freight To Pay (paid by consignee)' },
    { id: 'Freight Paid', name: 'Domestic — Freight Paid (paid by consignor)' },
    // Export / Import (Incoterms 2020)
    { id: 'EXW', name: 'Export/Import — EXW (Ex Works)' },
    { id: 'FCA', name: 'Export/Import — FCA (Free Carrier)' },
    { id: 'FAS', name: 'Export/Import — FAS (Free Alongside Ship)' },
    { id: 'FOB', name: 'Export/Import — FOB (Free On Board)' },
    { id: 'CFR', name: 'Export/Import — CFR (Cost and Freight)' },
    { id: 'CIF', name: 'Export/Import — CIF (Cost, Insurance & Freight)' },
    { id: 'CPT', name: 'Export/Import — CPT (Carriage Paid To)' },
    { id: 'CIP', name: 'Export/Import — CIP (Carriage & Insurance Paid To)' },
    { id: 'DAP', name: 'Export/Import — DAP (Delivered At Place)' },
    { id: 'DPU', name: 'Export/Import — DPU (Delivered At Place Unloaded)' },
    { id: 'DDP', name: 'Export/Import — DDP (Delivered Duty Paid)' },
];

// Delivery Challan types — incl. Returnable (DC-1).
export const CHALLAN_TYPE_CHOICES = [
    { id: 'Direct', name: 'Direct' },
    { id: 'Returnable', name: 'Returnable' },
    { id: 'Consignment', name: 'Consignment' },
    { id: 'Job Work', name: 'Job Work' },
    { id: 'Others', name: 'Others' },
];

// SO-2: Sales Order document type — drives the printed document title.
export const SO_DOCUMENT_TYPE_CHOICES = [
    { id: 'Proforma Invoice', name: 'Proforma Invoice' },
    { id: 'Tax Invoice', name: 'Tax Invoice' },
    { id: 'Direct', name: 'Direct' },
    { id: 'PO', name: 'PO' },
];
